import { useCallback, useState } from 'react';
import PageContainer from '../components/PageContainer';
import GeofenceMap from '../components/GeofenceMap';
import DistanceKmChart from '../components/DistanceKmChart';
import TimeInLocationChart from '../components/TimeInLocationChart';
import locationData from '../location_data.json';
import { ensureHistoryRows } from '../utils/demoData';
import drivingIcon from '../assets/driving.png';
import parkedIcon from '../assets/parked.png';
import inactiveIcon from '../assets/inactive.png';
import useAutoRefresh from '../hooks/useAutoRefresh';

export default function EquipmentLocation() {
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({ Driving: 0, Parked: 0, Inactive: 0 });

  const updateHistory = useCallback((rows) => {
    const dataset = ensureHistoryRows(rows, 250);
    setHistory(dataset);
    const countsByStatus = dataset.reduce(
      (acc, row) => {
        if (acc[row.current_status] !== undefined) {
          acc[row.current_status] += 1;
        }
        return acc;
      },
      { Driving: 0, Parked: 0, Inactive: 0 }
    );
    setCounts(countsByStatus);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch('/api/equipment_location.php');
      if (!res.ok) {
        throw new Error(`Failed to load history: ${res.status}`);
      }
      const json = await res.json();
      updateHistory(json);
    } catch (e) {
      console.error(e);
      updateHistory([]);
    }
  }, [updateHistory]);

  useAutoRefresh(fetchData, []);

  const panelClasses =
    'flex flex-col min-h-0 overflow-hidden rounded-[28px] border border-white/10 bg-white/70 p-4 shadow-xl shadow-slate-900/10 backdrop-blur-2xl sm:p-5 lg:p-6 dark:border-white/5 dark:bg-slate-900/60 dark:shadow-black/20';

  const statusMeta = {
    Driving: {
      gradient: 'from-emerald-400/90 to-emerald-500/70 text-emerald-950 dark:text-emerald-50',
      icon: drivingIcon,
      caption: 'Units currently driving',
    },
    Parked: {
      gradient: 'from-sky-400/90 to-sky-500/70 text-sky-950 dark:text-sky-50',
      icon: parkedIcon,
      caption: 'Assets staged or parked',
    },
    Inactive: {
      gradient: 'from-amber-400/90 to-amber-500/70 text-amber-950 dark:text-amber-50',
      icon: inactiveIcon,
      caption: 'Units awaiting dispatch',
    },
  };

  return (
    <PageContainer className="relative bg-slate-950 text-slate-100 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.18),transparent_55%),radial-gradient(circle_at_bottom,rgba(16,185,129,0.16),transparent_60%)]" />
      <div className="relative z-10 flex h-full min-h-0 flex-col gap-4 sm:gap-5 xl:gap-6 2xl:gap-8">
        <header className="flex flex-shrink-0 flex-col gap-4 sm:gap-5 lg:gap-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1.5">
              <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-200">
                Fleet Intelligence
              </span>
              <div>
                <h1 className="text-xl font-semibold leading-tight text-white sm:text-2xl md:text-3xl lg:text-4xl">
                  Equipment Location
                </h1>
                <p className="mt-2 max-w-2xl text-xs text-slate-300 sm:text-sm lg:text-base">
                  Monitor asset movement, manage geofences, and stay ahead of fleet activity with a live, data-rich dashboard.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] font-medium sm:gap-3 sm:text-xs md:text-sm">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-2 text-slate-200 transition hover:border-white/40 hover:bg-white/10"
              >
                Live Mode
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 via-sky-400 to-blue-500 px-4 py-2 text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:from-emerald-300 hover:via-sky-300 hover:to-blue-400"
              >
                Export Report
              </button>
            </div>
          </div>
          <div className="grid gap-2.5 sm:grid-cols-3 sm:gap-3 xl:gap-4">
            {Object.entries(counts).map(([status, value]) => {
              const meta = statusMeta[status];
              return (
              <div
                key={status}
                className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-white/10 p-3 transition hover:border-white/30 hover:bg-white/20 sm:p-4 xl:p-5 dark:border-white/5 dark:bg-white/5`}
              >
                <div
                  className={`absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br opacity-90 blur-2xl transition group-hover:opacity-100 ${
                    meta?.gradient ?? ''
                  }`}
                />
                <div className="relative z-10 flex items-center gap-3 sm:gap-3.5 lg:gap-4">
                  {meta?.icon && (
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-white/80 p-2 shadow-md shadow-slate-900/10 dark:bg-white/10 dark:shadow-black/40 sm:h-12 sm:w-12 xl:h-14 xl:w-14">
                      <img src={meta.icon} alt={`${status} icon`} className="h-8 w-8 object-contain sm:h-9 sm:w-9 xl:h-10 xl:w-10" />
                    </span>
                  )}
                  <div className="flex flex-1 flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-200">
                      {status}
                    </span>
                    <span className="text-2xl font-semibold text-white sm:text-3xl">{value}</span>
                    <span className="text-xs text-slate-300">{meta?.caption}</span>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </header>

        <div className="grid flex-1 min-h-0 gap-4 lg:gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] xl:gap-6 2xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <section className={`${panelClasses} flex-1`}>
            <div className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold text-white sm:text-base lg:text-lg">Live Equipment Map</h2>
              <p className="text-xs text-slate-300 sm:text-sm lg:text-base">
                Track assets, manage zones, and take immediate action from a unified view.
              </p>
            </div>
            <div className="mt-6 flex min-h-0 flex-1 flex-col">
              <GeofenceMap equipment={history} />
            </div>
          </section>

          <div className="grid min-h-0 flex-1 gap-6">
            <section className={`${panelClasses} flex min-h-0 flex-col`}>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-white sm:text-base lg:text-lg">Distance travelled (km)</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Rolling 30 days</span>
              </div>
              <div className="mt-4 flex min-h-0 flex-1">
                <DistanceKmChart data={locationData} />
              </div>
            </section>
            <section className={`${panelClasses} flex min-h-0 flex-col`}>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-white sm:text-base lg:text-lg">Time in Location (min)</h2>
                <span className="text-[11px] font-medium text-slate-300 sm:text-xs">Average dwell</span>
              </div>
              <div className="mt-4 flex min-h-0 flex-1">
                <TimeInLocationChart data={locationData} />
              </div>
            </section>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
